import { useState } from "react";
import { Loader2 } from "lucide-react";
import { type DevUser, devLogin } from "@/lib/dev-auth";

/**
 * Login picker for AUTH_MODE=dev. Replaces the OIDC sign-in button on the
 * login page when the API runs as a local dev environment. Styled to match
 * the login page's marketing register.
 */
export function DevLoginPanel({
  users,
  redirectTo,
}: {
  users: DevUser[];
  redirectTo: string;
}) {
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function pick(user: DevUser) {
    setError(null);
    setPending(user.key);
    try {
      await devLogin(user.key, redirectTo);
    } catch (err) {
      setError((err as Error).message);
      setPending(null);
    }
  }

  return (
    <fieldset className="space-y-3">
      <legend className="sr-only">Sign in as a seeded dev user</legend>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {users.map((user) => (
        <button
          key={user.key}
          type="button"
          onClick={() => pick(user)}
          disabled={pending !== null}
          className="flex w-full items-center justify-between gap-4 rounded-2xl border border-border bg-card px-5 py-4 text-left transition-colors hover:border-primary disabled:opacity-60"
        >
          <span className="min-w-0">
            <span className="block text-base font-semibold text-foreground">
              {user.displayName}
            </span>
            <span className="block text-sm text-muted-foreground">
              {user.summary}
            </span>
            <span className="block truncate font-mono text-xs text-muted-foreground">
              {user.email}
            </span>
          </span>
          {pending === user.key && (
            <Loader2
              className="h-4 w-4 shrink-0 animate-spin text-primary"
              aria-label="Signing in"
            />
          )}
        </button>
      ))}
    </fieldset>
  );
}
